import { Input } from "@design";
import { useQuery } from "@tanstack/react-query";
import { Database, HardDrive, Search } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { listSiteDrivesOptions, type SharePointDrive, type SharePointSite, searchSitesOptions } from "@/api";
import { InlineNotice } from "@/components/InlineNotice";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { DriveBrowser, type PickerSelection } from "./DriveBrowser";
import { NoMatches, OpenRow, RowsSkeleton } from "./DriveRows";
import { PickerPath } from "./PickerPath";

/** SharePoint: a site, then one of its document libraries, then that library's folders and files. */
export function SharePointBrowser({ selection }: Readonly<{ selection: PickerSelection }>) {
  const { t } = useTranslation("chat", { keyPrefix: "cloud" });
  const [site, setSite] = useState<SharePointSite | null>(null);
  const [drive, setDrive] = useState<SharePointDrive | null>(null);

  if (site && drive)
    return (
      <DriveBrowser
        key={drive.id}
        drive={drive}
        rootCrumbs={[site.display_name, drive.name]}
        searchLabel={t("searchIn", { name: drive.name })}
        onExit={() => setDrive(null)}
        selection={selection}
      />
    );
  if (site) return <LibraryList site={site} onBack={() => setSite(null)} onOpen={setDrive} />;
  return <SiteList onOpen={setSite} />;
}

function SiteList({ onOpen }: Readonly<{ onOpen: (site: SharePointSite) => void }>) {
  const { t } = useTranslation("chat", { keyPrefix: "cloud" });
  const [search, setSearch] = useState("");
  const query = useDebouncedValue(search.trim(), 250);
  const sites = useQuery(searchSitesOptions({ query: { q: query } }));
  const list = sites.data ?? [];

  return (
    <div className="flex flex-col gap-3">
      <PickerPath crumbs={[t("chooseSite")]} />
      <Input
        size="md"
        leadingIcon={<Search />}
        placeholder={t("searchSites")}
        aria-label={t("searchSites")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {sites.isError && <InlineNotice tone="error">{t("loadFailed")}</InlineNotice>}
      {sites.isPending && <RowsSkeleton />}
      {sites.isSuccess && list.length === 0 && <NoMatches title={t("noSites")} />}
      {list.length > 0 && (
        <ul className="flex flex-col">
          {list.map((site, i) => (
            <OpenRow
              key={site.id}
              icon={<Database size={14} />}
              label={site.display_name}
              description={site.description || undefined}
              divider={i > 0}
              onOpen={() => onOpen(site)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function LibraryList({
  site,
  onBack,
  onOpen,
}: Readonly<{ site: SharePointSite; onBack: () => void; onOpen: (drive: SharePointDrive) => void }>) {
  const { t } = useTranslation("chat", { keyPrefix: "cloud" });
  const [search, setSearch] = useState("");
  const drives = useQuery(listSiteDrivesOptions({ path: { site_id: site.id } }));
  const needle = search.trim().toLowerCase();
  const list = (drives.data ?? []).filter((d) => d.name.toLowerCase().includes(needle));

  return (
    <div className="flex flex-col gap-3">
      <PickerPath crumbs={[site.display_name]} onBack={onBack} />
      <Input
        size="md"
        leadingIcon={<Search />}
        placeholder={t("searchLibraries")}
        aria-label={t("searchLibraries")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {drives.isError && <InlineNotice tone="error">{t("loadFailed")}</InlineNotice>}
      {drives.isPending && <RowsSkeleton />}
      {drives.isSuccess && list.length === 0 && <NoMatches title={t("noLibraries")} />}
      {list.length > 0 && (
        <ul className="flex flex-col">
          {list.map((drive, i) => (
            <OpenRow
              key={drive.id}
              icon={<HardDrive size={14} />}
              label={drive.name}
              description={drive.item_count == null ? undefined : t("folderItems", { count: drive.item_count })}
              divider={i > 0}
              onOpen={() => onOpen(drive)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
